import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { BookUser, ChevronDown, FileCode2, FileText, IdCard, Wrench, LayoutDashboard, LogOut, Map as MapIcon, Menu, Moon, Package, ScanBarcode, ShieldCheck, Sun, X, MapPinned } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { peut, premierePage } from "../../lib/permissions";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const linkCls = ({ isActive }) =>
  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
    isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent hover:text-foreground"
  }`;

const subLinkCls = ({ isActive }) =>
  `px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
    isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
  }`;

// Onglets principaux du site. `subnav` (facultatif) : sous-onglets de la page
// courante, ex. [{ to: "/carte", label: "Carte", end: true }, ...].
export function AppShell({ children, fullHeight = false, title, subnav }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const cls = (i) => ({ isActive }) => linkCls({ isActive: isActive || (i.prefixe && (pathname === i.prefixe || pathname.startsWith(`${i.prefixe}/`))) });

  // Onglet visible selon les permissions ; il ouvre la première page permise.
  const items = [
    { to: "/", label: "Tableau de bord", icon: LayoutDashboard, perm: "onglet_dashboard", end: true, id: "dashboard" },
    { to: "/chauffeurs", label: "Fiches chauffeurs", icon: IdCard, perm: "onglet_chauffeurs", id: "chauffeurs" },
    { to: "/carte", label: "Carte", icon: MapIcon, perm: "onglet_carte", id: "carte" },
    { to: premierePage(user, "/scans"), label: "Remise de scan", icon: ScanBarcode, perm: "onglet_scans", id: "scans", prefixe: "/scans" },
    // L'onglet Colis s'ouvre sur « Colis » (recherche + chariots + cage) ; il reste actif sur toutes les pages /colis.
    { to: premierePage(user, "/colis"), label: "Colis", icon: Package, perm: "onglet_colis", id: "colis", prefixe: "/colis" },
    { to: "/admin", label: "Administrateur", icon: ShieldCheck, show: user?.is_admin, id: "admin" },
  ].filter((i) => (i.perm ? peut(user, i.perm) : i.show) && i.to);
  // Menu déroulant « Outils » : fonctions supplémentaires (la facture inter en dernier).
  const outils = [
    { to: "/outils/famis", label: "Script FAMIS", icon: FileCode2, perm: "onglet_famis", id: "famis" },
    { to: "/outils/registre", label: "Registre d'informations", icon: BookUser, perm: "onglet_registre", id: "registre" },
    { to: "/factures", label: "Génération facture inter", icon: FileText, perm: "onglet_factures", id: "factures" },
  ].filter((i) => peut(user, i.perm));
  const outilActif = outils.some((o) => pathname === o.to || pathname.startsWith(`${o.to}/`));
  const navigate = useNavigate();
  const subs = (subnav || []).filter((x) => x.show !== false && (!x.perm || peut(user, x.perm)));

  return (
    <div className={`flex flex-col bg-background print:bg-white ${fullHeight ? "h-[100dvh] overflow-hidden" : "min-h-screen"}`}>
      <header className="print:hidden shrink-0 z-30 border-b border-border bg-card/95 backdrop-blur px-3 sm:px-4 h-14 flex items-center gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <MapPinned size={17} />
          </div>
          <div className="min-w-0 hidden sm:block w-40 lg:w-48 shrink-0">
            <p className="font-heading font-bold leading-tight tracking-tight truncate">Carte des tournées</p>
            {title && <p className="text-[11px] text-muted-foreground leading-tight truncate">{title}</p>}
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1 ml-2 lg:ml-4">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={cls(i)} data-testid={`nav-${i.id}`} title={i.titre || i.label}>
              <i.icon size={16} /> <span className="hidden xl:inline">{i.label}</span>
            </NavLink>
          ))}
          {outils.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger className={linkCls({ isActive: outilActif }) + " outline-none"} data-testid="nav-outils" title="Outils">
                <Wrench size={16} /> <span className="hidden xl:inline">Outils</span> <ChevronDown size={14} className="opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[230px]">
                {outils.map((o) => (
                  <DropdownMenuItem key={o.to} onSelect={() => navigate(o.to)} data-testid={`nav-${o.id}`}
                    className={`gap-2 cursor-pointer ${pathname.startsWith(o.to) ? "font-semibold text-primary" : ""}`}>
                    <o.icon size={15} /> {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden 2xl:block text-sm text-muted-foreground mr-1 whitespace-nowrap">
            {user?.prenom} {user?.nom}
            {user?.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">admin</span>}
          </span>
          <button onClick={toggle} className="p-2 rounded-lg border border-border hover:bg-accent" aria-label="Basculer le thème" data-testid="theme-toggle">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button onClick={logout} className="hidden md:flex p-2 rounded-lg border border-border hover:bg-destructive/10 text-destructive" aria-label="Déconnexion" title="Déconnexion" data-testid="logout-button">
            <LogOut size={16} />
          </button>
          <button onClick={() => setOpen((o) => !o)} className="md:hidden p-2 rounded-lg border border-border" aria-label="Menu">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </header>

      {open && (
        <div className="md:hidden absolute top-14 inset-x-0 z-40 bg-card border-b border-border p-3 space-y-1 shadow-xl">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={cls(i)} onClick={() => setOpen(false)}>
              <i.icon size={16} /> {i.label}
            </NavLink>
          ))}
          {outils.length > 0 && (
            <div className="pt-2 mt-2 border-t border-border">
              <p className="px-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Wrench size={12} /> Outils</p>
              {outils.map((o) => (
                <NavLink key={o.to} to={o.to} className={linkCls} onClick={() => setOpen(false)}>
                  <o.icon size={16} /> {o.label}
                </NavLink>
              ))}
            </div>
          )}
          <div className="pt-2 mt-2 border-t border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{user?.prenom} {user?.nom}</span>
            <button onClick={logout} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10">
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      )}

      {subs.length > 0 && (
        <div className="print:hidden shrink-0 z-20 border-b border-border bg-card/80 backdrop-blur px-3 sm:px-4">
          <nav className="flex items-center gap-1 h-11 overflow-x-auto max-w-7xl" data-testid="subnav">
            {subs.map((x) => (
              <NavLink key={x.to} to={x.to} end={x.end} className={subLinkCls} data-testid={`subnav-${x.id || x.to.replace(/\//g, "-")}`}>
                {x.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      <main className={fullHeight ? "flex-1 min-h-0 relative" : "flex-1 p-4 sm:p-6 print:p-0 max-w-7xl w-full mx-auto"}>{children}</main>
    </div>
  );
}
